import React from 'react';
import {
    FileText,
    CreditCard,
    MapPin,
    Laptop,
    GraduationCap,
    DollarSign,
    ClipboardCheck,
    CalendarDays,
    Clock10,
    Settings,
    Plus,
    Map
} from 'lucide-react';
import {useLanguage} from '@/contexts/LanguageContext';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {useNavigate} from 'react-router-dom';

const Services = () => {
    const {t} = useLanguage();
    const navigate = useNavigate();

    const services = [
        {
            id: 'field_visit',
            title: 'خدمة زيارة ميدانية',
            titleEn: 'Field Visit Service',
            description: 'تسجيل بيانات ونتائج زيارة ميدانية',
            descriptionEn: 'Log the details and outcome of a field visit',
            icon: Map,
            href: '/services/field-visit'
        },
        {
            id: 'leave_request',
            title: 'طلب اجازة',
            titleEn: 'Leave Request',
            description: 'تقديم طلب إجازة سنوية أو مرضية أو طارئة',
            descriptionEn: 'Submit annual, sick, or emergency leave request',
            icon: CalendarDays,
            href: '/services/leave-request'
        },
        {
            id: 'salary_certificate',
            title: 'طلب تعريف راتب',
            titleEn: 'Salary Certificate Request',
            description: 'طلب الحصول على تعريف راتب للبنك أو جهات أخرى',
            descriptionEn: 'Request salary certificate for bank or other entities',
            icon: CreditCard,
            href: '/services/salary-certificate'
        },
        // {
        //   id: 'assignment_request',
        //   title: 'طلب انتداب',
        //   titleEn: 'Assignment Request',
        //   description: 'طلب انتداب للعمل في موقع أو مشروع آخر',
        //   descriptionEn: 'Request assignment to work at another location or project',
        //   icon: MapPin,
        //   href: '/services/assignment'
        // },
        {
            id: 'remote_work',
            title: 'طلب عمل عن بعد',
            titleEn: 'Remote Work Request',
            description: 'طلب العمل من المنزل أو موقع بعيد',
            descriptionEn: 'Request to work from home or remote location',
            icon: Laptop,
            href: '/services/remote-work'
        },
        {
            id: 'leave_permission',
            title: 'طلب إذن مغادرة',
            titleEn: 'Leave Permission Request',
            description: 'طلب إذن مغادرة من المكتب لفترة ',
            descriptionEn: 'Request permission to leave the office for a period',
            icon: Clock10,
            href: '/services/leave-permission'
        },
        {
            id: 'technical-support',
            title: 'طلب دعم فني',
            titleEn: 'Technical ٍupport Request',
            description: 'طلب دعم فني',
            descriptionEn: 'Request enrollment in technical support',
            icon: Settings,
            href: '/services/technical-support'
        },
        // {
        //   id: 'training_courses',
        //   title: 'طلب دورات',
        //   titleEn: 'Training Courses Request',
        //   description: 'طلب الالتحاق بدورات تدريبية أو تطوير مهني',
        //   descriptionEn: 'Request enrollment in training courses or professional development',
        //   icon: GraduationCap,
        //   href: '/services/training'
        // },
        // {
        //   id: 'compensation_allowance',
        //   title: 'طلب بدل تعويضات',
        //   titleEn: 'Compensation Allowance Request',
        //   description: 'طلب بدلات أو تعويضات مالية',
        //   descriptionEn: 'Request allowances or financial compensation',
        //   icon: DollarSign,
        //   href: '/services/compensation'
        // },
        // {
        //   id: 'annual_evaluation',
        //   title: 'طلب التقييم السنوي',
        //   titleEn: 'Annual Evaluation Request',
        //   description: 'طلب الحصول على نتائج التقييم السنوي',
        //   descriptionEn: 'Request annual performance evaluation results',
        //   icon: ClipboardCheck,
        //   href: '/services/evaluation'
        // }
    ];

    const handleServiceClick = (service: typeof services[0]) => {
        if (service.id === 'salary_certificate') {
            navigate('/services/salary-certificate');
        } else if (service.id === 'remote_work') {
            navigate('/services/remote-work');
        } else if (service.id === 'leave_permission') {
            navigate('/services/leave-permission');
        } else if (service.id === 'leave_request') {
            navigate('/services/leave-request');
        } else if (service.id === 'technical-support') {
            navigate('/services/technical-support');
        } else if (service.id === 'field_visit') {
            navigate('/services/field-visit');
        } else {
            // For other services, navigate to a placeholder or log message
            navigate('/services');
        }
        console.log(`Navigating to ${service.id}`);
    };

    return (
        <div className="w-full space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight">{t('services.title')}</h1>
                    <p className="text-muted-foreground mt-2 sm:text-lg">{t('services.description')}</p>
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {services.map((service) => {
                    const Icon = service.icon;
                    return (
                        <div
                            key={service.id}
                            role="button"
                            tabIndex={0}
                            className="surface group flex flex-col p-6 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)]"
                            onClick={() => handleServiceClick(service)}
                            onKeyDown={(e) => e.key === 'Enter' && handleServiceClick(service)}
                        >
                            <div className="w-12 h-12 rounded-2xl bg-ink text-white flex items-center justify-center transition-colors group-hover:bg-primary">
                                <Icon className="h-6 w-6"/>
                            </div>
                            <h3 className="mt-5 text-lg font-bold">
                                {service.title}
                            </h3>
                            <p className="mt-1 flex-1 text-sm text-muted-foreground">
                                {service.description}
                            </p>
                            <Button
                                variant="secondary"
                                className="mt-5 w-full gap-2 group-hover:bg-primary group-hover:text-primary-foreground"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleServiceClick(service);
                                }}
                            >
                                <Plus className="h-4 w-4"/>
                                {t('services.start_request')}
                            </Button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default Services;